import { API_BASE_URL } from "../../../../config/api";

// Helper function to get auth token
// NOTE: the rest of the app stores the JWT under "access_token" (see services/api.ts);
// previously this file read from "token" which returned null, so the backend received
// "Bearer null". That was silent while the sync endpoint didn't validate the JWT, but
// the user-token-based sync now relies on a valid JWT to identify the caller.
const getAuthToken = (): string | null => {
  return localStorage.getItem("access_token");
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export interface ZohoSyncAccountsResponse {
  success: boolean;
  message: string;
  data: {
    total_fetched: number;
    inserted: number;
    updated: number;
  };
}

export interface ZohoSyncTransactionsResponse {
  success: boolean;
  message: string;
  data: {
    accounts_synced: number;
    transactions_synced: number;
    transactions_inserted: number;
    transactions_updated: number;
  };
}

export interface ZohoSyncStatusResponse {
  success: boolean;
  data: {
    is_running: boolean;
    running_sync: any;
    last_sync: any;
    accounts_count: number;
    active_accounts_count: number;
    transactions_count: number;
    recent_logs: any[];
  };
}

export interface TruncateResponse {
  success: boolean;
  message: string;
  data?: {
    deleted_count?: number;
  };
}

/**
 * Sync Chart of Accounts from Zoho
 * @param activeOnly If true, only sync active accounts
 */
export const syncZohoAccounts = async (activeOnly: boolean = false): Promise<ZohoSyncAccountsResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/zoho/sync/accounts?active_only=${activeOnly}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to sync accounts: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

/**
 * Sync Transactions from Zoho
 * @param activeOnly If true, only sync transactions for active accounts
 * @param fromDate Optional date to sync transactions after (YYYY-MM-DD format)
 */
export const syncZohoTransactions = async (
  activeOnly: boolean = false,
  fromDate?: string
): Promise<ZohoSyncTransactionsResponse> => {
  const params = new URLSearchParams();
  params.append('active_only', activeOnly.toString());
  if (fromDate) {
    params.append('from_date', fromDate);
  }

  const response = await fetch(`${API_BASE_URL}/api/zoho/sync/transactions?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to sync transactions: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

export interface ZohoSyncTransactionsDebugResponse {
  success: boolean;
  message: string;
  data: {
    active_only: boolean;
    from_date_param: string | null;
    total_accounts: number;
    breakdown_by_type: Record<string, number>;
    accounts: Array<{
      zoho_account_id: string;
      account_type: string;
      is_active: number | boolean | null;
      last_synced_at: string | null;
      account_name: string;
    }>;
  };
}

/**
 * Dry-run Sync Transactions: lists accounts that would be synced, WITHOUT calling Zoho.
 * Backend exits immediately after building the list. Useful for verifying coverage.
 */
export const syncZohoTransactionsDebug = async (
  activeOnly: boolean = true
): Promise<ZohoSyncTransactionsDebugResponse> => {
  const params = new URLSearchParams();
  params.append('active_only', activeOnly.toString());
  params.append('debug_only', '1');

  const response = await fetch(`${API_BASE_URL}/api/zoho/sync/transactions?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed debug sync: ${response.statusText}`,
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Get Zoho sync status
 */
export const getZohoSyncStatus = async (): Promise<ZohoSyncStatusResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/zoho/sync/status`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to get sync status: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

/**
 * Truncate (delete all) Chart of Accounts
 */
export const truncateZohoAccounts = async (): Promise<TruncateResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/zoho/truncate/accounts`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to truncate accounts: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

/**
 * Truncate (delete all) Transactions
 */
export const truncateZohoTransactions = async (): Promise<TruncateResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/zoho/truncate/transactions`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to truncate transactions: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

export interface ZohoSyncGlMappingsResponse {
  success: boolean;
  message: string;
  data: {
    created: number;
    updated: number;
    matched: number;
    total_processed: number;
  };
}

/**
 * Sync Zoho accounts to GL mappings
 * Creates GL mappings in cfo_mcoa from synced Zoho Chart of Accounts
 */
export const syncZohoGlMappings = async (companyId?: number): Promise<ZohoSyncGlMappingsResponse> => {
  const body: any = {};
  if (companyId) {
    body.company_id = companyId;
  }

  const response = await fetch(`${API_BASE_URL}/api/zoho/sync/gl-mappings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to sync GL mappings: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

/**
 * Truncate (delete all) GL Mappings
 */
export const truncateZohoGlMappings = async (): Promise<TruncateResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/zoho/truncate/gl-mappings`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to truncate GL mappings: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

export interface ZohoSyncTrialBalanceResponse {
  success: boolean;
  message: string;
  data: {
    months_processed: number;
    accounts_processed: number;
    records_inserted: number;
    records_updated: number;
    records_skipped: number;
    accounts_without_gl_mapping: number;
  };
}

/**
 * Sync Trial Balance from Zoho for a date range
 * @param fromDate Start date (YYYY-MM-DD format)
 * @param toDate End date (YYYY-MM-DD format)
 * @param companyId Optional company ID
 */
export const syncZohoTrialBalance = async (
  fromDate: string,
  toDate: string,
  companyId?: number
): Promise<ZohoSyncTrialBalanceResponse> => {
  const body: any = {
    from_date: fromDate,
    to_date: toDate,
  };
  if (companyId) {
    body.company_id = companyId;
  }

  const response = await fetch(`${API_BASE_URL}/api/zoho/sync/trial-balance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to sync trial balance: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

/**
 * Truncate (delete all) Trial Balance
 */
export const truncateZohoTrialBalance = async (): Promise<TruncateResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/zoho/truncate/trial-balance`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to truncate trial balance: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.json();
};

/**
 * Download Trial Balance Month-wise as Excel
 * @param fromDate Start date (YYYY-MM-DD format)
 * @param toDate End date (YYYY-MM-DD format)
 * @param companyId Optional company ID
 */
export const downloadZohoTrialBalanceMonthWise = async (
  fromDate: string,
  toDate: string,
  companyId?: number
): Promise<Blob> => {
  const params = new URLSearchParams();
  params.append('from_date', fromDate);
  params.append('to_date', toDate);
  if (companyId) {
    params.append('company_id', companyId.toString());
  }

  const response = await fetch(`${API_BASE_URL}/api/zoho/download/trial-balance-monthwise?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `Failed to download trial balance: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return response.blob();
};
